// ---------------------------------------------------------------------------
// DnD Email Editor – Core State Hook
//
// Manages: model state, undo/redo, selection, dirty tracking,
// debounced HTML compilation.
// ---------------------------------------------------------------------------

import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import type {
  DndEmailModel,
  Layer,
  Element,
  GlobalStyles,
  WrapperSettings,
  Column,
  LayerType,
  ElementType,
} from './dnd-model';
import {
  addLayer as modelAddLayer,
  addLayers as modelAddLayers,
  removeLayer as modelRemoveLayer,
  moveLayer as modelMoveLayer,
  duplicateLayer as modelDuplicateLayer,
  updateLayer as modelUpdateLayer,
  addElement as modelAddElement,
  removeElement as modelRemoveElement,
  moveElement as modelMoveElement,
  updateElement as modelUpdateElement,
  updateColumn as modelUpdateColumn,
  duplicateElement as modelDuplicateElement,
  updateGlobalStyles as modelUpdateGlobalStyles,
  updateWrapper as modelUpdateWrapper,
  findLayerById,
  findElementById,
} from './dnd-model';
import { createEmptyModel, createLayerByType, createElementByType } from './dnd-defaults';
import { compileModel } from './dnd-compiler';
import { compileToPlainText } from './dnd-plain-text';

// ---- Types ----------------------------------------------------------------

export interface SelectionState {
  layerId: string | null;
  elementId: string | null;
  columnId: string | null;
}

export interface UseDndEditorReturn {
  // Model
  model: DndEmailModel;
  compiledHtml: string;
  plainText: string;

  // Selection
  selection: SelectionState;
  selectLayer: (layerId: string | null) => void;
  selectElement: (layerId: string, columnId: string, elementId: string) => void;
  selectColumn: (layerId: string, columnId: string) => void;
  clearSelection: () => void;
  selectedLayer: Layer | undefined;
  selectedElement: { layer: Layer; column: Column; element: Element } | undefined;

  // Layer CRUD
  addLayerByType: (type: LayerType, index?: number) => string;
  addLayerInstance: (layer: Layer, index?: number) => void;
  addLayersInstance: (layers: Layer[], index?: number) => void;
  removeSelectedLayer: () => void;
  removeLayerById: (id: string) => void;
  moveLayerByIndex: (from: number, to: number) => void;
  duplicateLayerById: (id: string) => void;
  updateLayerById: (id: string, patch: Partial<Layer>) => void;

  // Element CRUD
  addElementByType: (
    layerId: string,
    columnId: string,
    type: ElementType,
    index?: number,
  ) => string;
  addElementInstance: (
    layerId: string,
    columnId: string,
    element: Element,
    index?: number,
  ) => void;
  removeElementById: (
    layerId: string,
    columnId: string,
    elementId: string,
  ) => void;
  moveElementByIndex: (
    layerId: string,
    fromColId: string,
    fromIdx: number,
    toColId: string,
    toIdx: number,
  ) => void;
  updateElementById: (
    layerId: string,
    columnId: string,
    elementId: string,
    patch: Partial<Element>,
  ) => void;
  updateColumnById: (
    layerId: string,
    columnId: string,
    patch: Partial<Column>,
  ) => void;
  duplicateElementById: (
    layerId: string,
    columnId: string,
    elementId: string,
  ) => void;

  // Global
  setGlobalStyles: (patch: Partial<GlobalStyles>) => void;
  setWrapper: (patch: Partial<WrapperSettings>) => void;
  setPreheaderText: (text: string) => void;

  // Undo / Redo
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;

  // Dirty tracking
  isDirty: boolean;
  markSaved: () => void;
  revert: () => void;
  canRevert: boolean;

  // Reset
  setModel: (model: DndEmailModel) => void;
}

// ---- Constants ------------------------------------------------------------

const MAX_UNDO = 50;
const COMPILE_DEBOUNCE_MS = 300;

// ---- Hook -----------------------------------------------------------------

export function useDndEditor(
  initialModel?: DndEmailModel,
): UseDndEditorReturn {
  const [model, setModelRaw] = useState<DndEmailModel>(
    () => initialModel ?? createEmptyModel(),
  );
  const [selection, setSelection] = useState<SelectionState>({
    layerId: null,
    elementId: null,
    columnId: null,
  });

  // Undo/redo stacks
  const undoStack = useRef<DndEmailModel[]>([]);
  const redoStack = useRef<DndEmailModel[]>([]);

  // Dirty tracking
  //
  // `savedModelRef` is the serialized baseline of the last-saved model. It's a
  // ref (not state) because mutating it must not itself trigger a render, but
  // that means `markSaved` can't recompute `isDirty` on its own — so it bumps
  // `savedVersion`, which the memo below depends on, to force a recompute after
  // a save. Without this the indicator would stay "dirty" until the next edit.
  const savedModelRef = useRef<string>(JSON.stringify(model));
  const [savedVersion, setSavedVersion] = useState(0);
  const isDirty = useMemo(
    () => JSON.stringify(model) !== savedModelRef.current,
    [model, savedVersion],
  );

  // Compiled output (debounced)
  const [compiledHtml, setCompiledHtml] = useState(() => compileModel(model));
  const [plainText, setPlainText] = useState(() => compileToPlainText(model));
  const compileTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (compileTimerRef.current) clearTimeout(compileTimerRef.current);
    compileTimerRef.current = setTimeout(() => {
      setCompiledHtml(compileModel(model));
      setPlainText(compileToPlainText(model));
    }, COMPILE_DEBOUNCE_MS);
    return () => {
      if (compileTimerRef.current) clearTimeout(compileTimerRef.current);
    };
  }, [model]);

  // ---- Internal state update with undo snapshot ---------------------------

  const pushUndo = useCallback((prev: DndEmailModel) => {
    undoStack.current = [...undoStack.current.slice(-MAX_UNDO + 1), prev];
    redoStack.current = [];
  }, []);

  const applyModel = useCallback(
    (updater: (prev: DndEmailModel) => DndEmailModel) => {
      setModelRaw((prev) => {
        pushUndo(prev);
        return updater(prev);
      });
    },
    [pushUndo],
  );

  // ---- Selection ----------------------------------------------------------

  const selectLayer = useCallback((layerId: string | null) => {
    setSelection({ layerId, elementId: null, columnId: null });
  }, []);

  const selectElement = useCallback(
    (layerId: string, columnId: string, elementId: string) => {
      setSelection({ layerId, elementId, columnId });
    },
    [],
  );

  const selectColumn = useCallback((layerId: string, columnId: string) => {
    setSelection({ layerId, columnId, elementId: null });
  }, []);

  const clearSelection = useCallback(() => {
    setSelection({ layerId: null, elementId: null, columnId: null });
  }, []);

  const selectedLayer = useMemo(
    () => (selection.layerId ? findLayerById(model, selection.layerId) : undefined),
    [model, selection.layerId],
  );

  const selectedElement = useMemo(() => {
    if (!selection.elementId) return undefined;
    return findElementById(model, selection.elementId) ?? undefined;
  }, [model, selection.elementId]);

  // ---- Layer CRUD ---------------------------------------------------------

  const addLayerByType = useCallback(
    (type: LayerType, index?: number) => {
      const layer = createLayerByType(type);
      applyModel((m) => modelAddLayer(m, layer, index));
      selectLayer(layer.id);
      return layer.id;
    },
    [applyModel, selectLayer],
  );

  const addLayerInstance = useCallback(
    (layer: Layer, index?: number) => {
      applyModel((m) => modelAddLayer(m, layer, index));
      selectLayer(layer.id);
    },
    [applyModel, selectLayer],
  );

  const addLayersInstance = useCallback(
    (layers: Layer[], index?: number) => {
      if (layers.length === 0) return;
      applyModel((m) => modelAddLayers(m, layers, index));
      selectLayer(layers[0].id);
    },
    [applyModel, selectLayer],
  );

  const removeLayerById = useCallback(
    (id: string) => {
      applyModel((m) => modelRemoveLayer(m, id));
      setSelection((s) => (s.layerId === id ? { layerId: null, elementId: null, columnId: null } : s));
    },
    [applyModel],
  );

  const removeSelectedLayer = useCallback(() => {
    if (selection.layerId) removeLayerById(selection.layerId);
  }, [selection.layerId, removeLayerById]);

  const moveLayerByIndex = useCallback(
    (from: number, to: number) => {
      applyModel((m) => modelMoveLayer(m, from, to));
    },
    [applyModel],
  );

  const duplicateLayerById = useCallback(
    (id: string) => {
      applyModel((m) => modelDuplicateLayer(m, id));
    },
    [applyModel],
  );

  const updateLayerById = useCallback(
    (id: string, patch: Partial<Layer>) => {
      applyModel((m) => modelUpdateLayer(m, id, patch));
    },
    [applyModel],
  );

  // ---- Element CRUD -------------------------------------------------------

  const addElementByType = useCallback(
    (layerId: string, columnId: string, type: ElementType, index?: number) => {
      const element = createElementByType(type);
      applyModel((m) => modelAddElement(m, layerId, columnId, element, index));
      setSelection({ layerId, columnId, elementId: element.id });
      return element.id;
    },
    [applyModel],
  );

  const addElementInstance = useCallback(
    (layerId: string, columnId: string, element: Element, index?: number) => {
      applyModel((m) => modelAddElement(m, layerId, columnId, element, index));
      setSelection({ layerId, columnId, elementId: element.id });
    },
    [applyModel],
  );

  const removeElementById = useCallback(
    (layerId: string, columnId: string, elementId: string) => {
      applyModel((m) => modelRemoveElement(m, layerId, columnId, elementId));
      setSelection((s) =>
        s.elementId === elementId
          ? { layerId, elementId: null, columnId: null }
          : s,
      );
    },
    [applyModel],
  );

  const moveElementByIndex = useCallback(
    (
      layerId: string,
      fromColId: string,
      fromIdx: number,
      toColId: string,
      toIdx: number,
    ) => {
      applyModel((m) =>
        modelMoveElement(m, layerId, fromColId, fromIdx, toColId, toIdx),
      );
    },
    [applyModel],
  );

  const updateElementById = useCallback(
    (
      layerId: string,
      columnId: string,
      elementId: string,
      patch: Partial<Element>,
    ) => {
      applyModel((m) =>
        modelUpdateElement(m, layerId, columnId, elementId, patch),
      );
    },
    [applyModel],
  );

  const updateColumnById = useCallback(
    (layerId: string, columnId: string, patch: Partial<Column>) => {
      applyModel((m) => modelUpdateColumn(m, layerId, columnId, patch));
    },
    [applyModel],
  );

  const duplicateElementById = useCallback(
    (layerId: string, columnId: string, elementId: string) => {
      applyModel((m) => modelDuplicateElement(m, layerId, columnId, elementId));
    },
    [applyModel],
  );

  // ---- Global / Wrapper ---------------------------------------------------

  const setGlobalStyles = useCallback(
    (patch: Partial<GlobalStyles>) => {
      applyModel((m) => modelUpdateGlobalStyles(m, patch));
    },
    [applyModel],
  );

  const setWrapper = useCallback(
    (patch: Partial<WrapperSettings>) => {
      applyModel((m) => modelUpdateWrapper(m, patch));
    },
    [applyModel],
  );

  const setPreheaderText = useCallback(
    (text: string) => {
      applyModel((m) => ({ ...m, preheaderText: text }));
    },
    [applyModel],
  );

  // ---- Undo / Redo --------------------------------------------------------

  const canUndo = undoStack.current.length > 0;
  const canRedo = redoStack.current.length > 0;

  const undo = useCallback(() => {
    if (!undoStack.current.length) return;
    const prev = undoStack.current[undoStack.current.length - 1];
    undoStack.current = undoStack.current.slice(0, -1);
    setModelRaw((current) => {
      redoStack.current = [...redoStack.current, current];
      return prev;
    });
  }, []);

  const redo = useCallback(() => {
    if (!redoStack.current.length) return;
    const next = redoStack.current[redoStack.current.length - 1];
    redoStack.current = redoStack.current.slice(0, -1);
    setModelRaw((current) => {
      undoStack.current = [...undoStack.current, current];
      return next;
    });
  }, []);

  // ---- Dirty tracking -----------------------------------------------------

  const markSaved = useCallback(() => {
    savedModelRef.current = JSON.stringify(model);
    // Force `isDirty` to recompute against the new baseline (ref writes alone
    // don't re-render).
    setSavedVersion((v) => v + 1);
  }, [model]);

  const canRevert = isDirty;

  const revert = useCallback(() => {
    const saved = JSON.parse(savedModelRef.current) as DndEmailModel;
    if (JSON.stringify(model) !== savedModelRef.current) {
      pushUndo(model);
      setModelRaw(saved);
      setSelection({ layerId: null, elementId: null, columnId: null });
    }
  }, [model, pushUndo]);

  // ---- Full model replace (for loading templates) -------------------------

  const setModel = useCallback(
    (newModel: DndEmailModel) => {
      pushUndo(model);
      setModelRaw(newModel);
      clearSelection();
    },
    [model, pushUndo, clearSelection],
  );

  return {
    model,
    compiledHtml,
    plainText,
    selection,
    selectLayer,
    selectElement,
    selectColumn,
    clearSelection,
    selectedLayer,
    selectedElement,
    addLayerByType,
    addLayerInstance,
    addLayersInstance,
    removeSelectedLayer,
    removeLayerById,
    moveLayerByIndex,
    duplicateLayerById,
    updateLayerById,
    addElementByType,
    addElementInstance,
    removeElementById,
    moveElementByIndex,
    updateElementById,
    updateColumnById,
    duplicateElementById,
    setGlobalStyles,
    setWrapper,
    setPreheaderText,
    undo,
    redo,
    canUndo,
    canRedo,
    isDirty,
    markSaved,
    revert,
    canRevert,
    setModel,
  };
}
